/**
 * Interface for StillImageBackground background images config
 */
import {IGameModeChangedData, GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import { Sprite, Point, Graphics, Container, BLEND_MODES  } from "pixi.js";
import { TimelineMax, Power2, Timeline, TweenLite, Bounce, Power3, Tween, Power0, TimelineLite } from "gsap";
import { EventHandler } from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import { GameEvent } from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import { SpinEvent } from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import { ResizeHelper } from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";

import { IStillImageBackgroundImagesConfig, IStillImageBackgroundConfig } from "@nolimitcity/slot-game/bin/game/background/StillImageBackground"
import { Background } from "@nolimitcity/slot-game/bin/core/background/Background";
import { SanSymbolData } from "../../engine/Symbol/SanSymbolData";
import { SanEngineManager } from "./../../engine/SanEngineManager";
import { MathHelper } from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import { StageManager } from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import { SanGameConfig } from "../../SanGameConfig";
import { SpinePlayer } from "../../proto/SpinePlayer";
import { StateHandler } from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import { Sequence } from "@nolimitcity/slot-game/bin/core/displayobject/Sequence";
import { SoundManager } from "../../resource/sound/SoundManager";
import { SoundConfig } from "../../SoundConfig";
import { JumpingWildCollector } from "../independent/collector/JumpingWildCollector";
import { XWaysCounter } from "../independent/countup/XWaysCounter";

class Cloud extends PIXI.Container {

    private cloudTypes:string[] = ["BG_Slices/BG_Cloud","BG_Slices/BG_Cloud_2","BG_Slices/BG_Cloud_3"]
    private amountOfClouds:number = 15;

    private get fetchCloud():PIXI.Texture[]{
        const rand:number = Math.floor(Math.random()*this.cloudTypes.length)%this.cloudTypes.length;
        return GameResources.getTextures(this.cloudTypes[rand]);
    }

    constructor(){
        super();
        this.createClouds();
    }

    private createClouds():void{

        let offsetX:number = -300;
        let offsetY:number = -300;

        for(let i:number = 0; i < this.amountOfClouds; i++){
            const seq:Sequence = Sequence.createSequence(this.fetchCloud);
            const randScale:number = 0.35 + (Math.random()*0.65);
            seq.scale.set(randScale, randScale);
            seq.position.set(offsetX,offsetY);

            seq.scale.x = Math.random() > 0.5 ? seq.scale.x : -seq.scale.x;

            offsetX += 1000/this.amountOfClouds + Math.random() * 80;
            offsetY = -300 + Math.random() * 600;
            seq.alpha = 1;
            seq.playAnimation({repeat:-1}).to(seq.position,1, {x: "+=" + Math.abs(seq.scale.x*40), ease:Power0.easeNone});
            seq.timeline!.eventCallback("onRepeat", () => {
                seq.timeline!.invalidate();
                if(seq.x > 1280){
                    seq.scale.x = Math.random() > 0.5 ? seq.scale.x : -seq.scale.x;
                    seq.x = -(1280/2 + 100*Math.random()) ;
                    seq.y = -300 + Math.random() * 600;
                    seq.setTexture(this.fetchCloud);
                } 
            })
            this.addChild(seq);
        }
        this.setActive(true);
    }

    public setActive(value:boolean):void{
        (<Sequence[]>this.children).forEach(s => {
            (value ? s.resume(): s.pause());
            this.visible = value;
        });
    }
}

export class BgSpineMode extends StateHandler{

    public static readonly NORMAL:string = "BG_Main";
    public static readonly NORMAL_IDLE:string = "BG_Main_Idle";
    public static readonly NORMAL_TO_FS:string = "BG_Main_To_FS";
    public static readonly FS_TO_NORMAL:string = "BG_FS_To_Main";
    public static readonly FS_IDLE:string = "BG_FS";
}

export class SanBackground extends Background {

    private static _instance:SanBackground;
    protected readonly _backgroundImages:IStillImageBackgroundImagesConfig;
    protected _backgroundSprite:Sprite;
    protected _graphOverlay:Graphics;

    protected _superGraphOverlay:Graphics;

    protected _reelBackground:Sprite;
    protected _timeline:TimelineMax;
    protected _timeline_sky:TimelineMax;

    protected tl_logo:TimelineMax;

    protected tl_spine:TimelineMax;
    private cheering_spine:SpinePlayer;
    private stoppingSpine:boolean = true;

    private clouds:Cloud;

    private bg_spine:SpinePlayer;

    private bg_FS_Glow:PIXI.Sprite;
    private bg_FS_Fires:Sequence[];

    private bg_FS:PIXI.Sprite;
    private bg_Sky:PIXI.Sprite;
    private bg_Sky_dark:PIXI.Sprite;

    private bg_fs_spine_attention:SpinePlayer;

    public static get instance():SanBackground{
        return this._instance;
    }

    public couldsActive(value:boolean):void{
        return this.clouds.setActive(value);
    }

    constructor(config:IStillImageBackgroundConfig) {
        super(config);
        this._backgroundImages = config.backgroundImages;
        SanBackground._instance = this;
        this.buttonMode = false;
        this.on('pointertap', () => EventHandler.dispatchEvent(new GameEvent(SpinEvent.SKIP)));
    }

    public setSkipAble(value:boolean):void { this.interactive = value; } 

    public changeBgManiacBlackness(_alpha:number, duration:number):TweenLite{
        return TweenLite.fromTo(this._superGraphOverlay,duration,{alpha:0},{alpha:_alpha});
    }

    public changeBgBlackness(duration:number, _alpha:number):TimelineMax{
        this._timeline = Helper.timeLineKiller(this._timeline,new TimelineMax(),true);
        this._timeline.to(this._graphOverlay,duration,{alpha:_alpha});
        return this._timeline;
    }

    public playAttentionBlackFlash(duration:number,_alphaMax:number, _alpha:number):TimelineMax{
        this._timeline = Helper.timeLineKiller(this._timeline,new TimelineMax());

        if(!this.bg_FS_Fires[0].visible){
            this._timeline.to(this._graphOverlay,duration,{alpha:_alphaMax,ease:Power2.easeOut});
            this._timeline.to(this._graphOverlay,duration*2,{alpha:_alpha,ease:Power2.easeOut});
        }

        return this._timeline;
    }

    public changeSkyToBlack(alpha:number):TimelineMax{
        this._timeline_sky = Helper.timeLineKiller(this._timeline_sky, new TimelineMax());
        if(this.bg_Sky_dark.alpha == alpha) return this._timeline_sky;
        this._timeline_sky.to(this.bg_Sky_dark,2,{alpha:alpha});
        return this._timeline_sky;
    }

    public restoreOverlay(duration:number):TimelineMax{
        this._timeline = Helper.timeLineKiller(this._timeline,new TimelineMax());
        if(this._graphOverlay.alpha != 0) this._timeline.to(this._graphOverlay,duration,{alpha:0,ease:Power2.easeOut},0);
        if(this.bg_Sky_dark.alpha != 0) this._timeline.to(this.bg_Sky_dark,duration,{alpha:0,ease:Power2.easeOut},0);
        if(this._superGraphOverlay.alpha != 0) this._timeline.to(this._superGraphOverlay,duration,{alpha:0,ease:Power2.easeOut},0);
        return this._timeline;
    }

    public moveBG(goDown:boolean):TweenLite[]{
        
        const moveDuration:number = 3;

        const twArr:TweenLite[] = [];
        if(goDown){
            twArr.push(TweenLite.fromTo(this.bg_spine.position,moveDuration,{y:360}, {y:360 - 1680, ease:Power3.easeOut}));
            twArr.push(TweenLite.fromTo(this.bg_FS.position,moveDuration,{y:-480 + this.bg_FS.texture.height}, {y:-480, ease:Power3.easeOut}));
            twArr.push(...JumpingWildCollector.instance.changeModePos(true));
            twArr.push(...XWaysCounter.instance.changeModePos(true));

            twArr[1].eventCallback("onComplete", () => {this.bg_spine.stop(); this.clouds.setActive(false)});
            twArr[0].eventCallback("onStart", () => { SoundManager.playSoundEffect(SoundConfig.instance.BG_GOING_DOWN) });
        }else{
            this.bg_spine.play({name:"BG_Main_Idle", loop: true})
            this.clouds.setActive(true);
            twArr.push(...JumpingWildCollector.instance.changeModePos(false));
            twArr.push(...XWaysCounter.instance.changeModePos(false));

            twArr.push(TweenLite.fromTo(this.bg_spine.position,moveDuration/2,{y:360 - 1680}, {y:360, ease:Power3.easeOut}));
            twArr.push(TweenLite.fromTo(this.bg_FS.position,moveDuration/2,{y:-480 }, {y:-480+ this.bg_FS.texture.height, ease:Power3.easeOut}));
            twArr[0].eventCallback("onStart", () => { SoundManager.playSoundEffect(SoundConfig.instance.BG_GOING_UP) });
        }

        return twArr;
    }

    protected initAnimations():void {
        super.initAnimations();

        this.bg_Sky = this.addChild(new PIXI.Sprite(GameResources.getTextures("BG_Slices/Sky")[0]))
        this.bg_Sky.position.set(-480,-480);

        this.bg_Sky_dark = this.addChild(new PIXI.Sprite(GameResources.getTextures("BG_Slices/Sky")[0]))
        this.bg_Sky_dark.position.set(-480,-480);
        this.bg_Sky_dark.tint = 0x000001;

        this.bg_Sky_dark.alpha = 0;

        this.clouds = this.addChild(new Cloud());
        
        this.bg_FS = this.addChild(new PIXI.Sprite(GameResources.getTextures("FSBackground")[0]))
        this.bg_FS.position.set(-480, -480 + this.bg_FS.texture.height);

        this.bg_spine = this.addChild(new SpinePlayer("bg"));
        this.bg_spine.position.set(360,360);

        this._graphOverlay = new Graphics();
        this.addChild(this._graphOverlay);
        this._graphOverlay.alpha = 0;

        this.bg_FS_Glow = this.addChild(new PIXI.Sprite(GameResources.getTextures("BG_Slices/BG_Rays")[0]));
        this.bg_FS_Glow.position.set(-480,-480);
        this.bg_FS_Glow.blendMode = BLEND_MODES.ADD;
        this.bg_FS_Glow.visible = false;
        
        this.createReelFire();

        this.bg_fs_spine_attention = this.addChild(new SpinePlayer("FS_Reels_Attention"));
        this.bg_fs_spine_attention.position.set(360,360);

        this._superGraphOverlay = new Graphics();
        StageManager.getLayer(SanGameConfig.instance.LAYERS.DARK_AREA.name).addChild(this._superGraphOverlay);
        this._superGraphOverlay.alpha = 0;
        
        this.bg_spine.play({name:"BG_Main_Idle", loop: true});

        this._reelBackground = this.addChild(new Sprite(GameResources.getTextures("Reel_BG")[0]));//GameResources.getTextures(this._backgroundImages[GameMode.NORMAL].image)[0])
        this._reelBackground.anchor.set(0.5,0.5);

        this.cheering_spine = StageManager.getLayer(SanGameConfig.instance.LAYERS.FX_WIN.name).addChild(new SpinePlayer("skele"));
        this.cheering_spine.position.set(360,360);
    }

    // public fsSpineBGs(lvl:number):void{
    //     if(!lvl)
    //         this.bg_fs_spine_attention.stop();
    //     else
    //         this.bg_fs_spine_attention.play({name:"Level_"+(lvl+1),loop:true});
    // }

    public fsSpineBG(lvl:number):void{
        if(!lvl){
            TweenLite.to(this.bg_fs_spine_attention,0.3,{alpha:0}).eventCallback("onComplete", () => this.bg_fs_spine_attention.stop());
        }
        else{
            this.bg_fs_spine_attention.play({name:"Level_"+(Math.min(lvl+1,2)),loop:true});
            TweenLite.fromTo(this.bg_fs_spine_attention,1,{alpha:0},{alpha:1});
        }
    }

    private createReelFire():void{
        this.bg_FS_Fires = [];

        for(let fire:number = 0; fire < 4; fire++){
            const f:Sequence = new Sequence({textures:GameResources.getTextures("FIYA")});
            f.blendMode = BLEND_MODES.ADD;
            f.visible = false;

            switch(fire){
                case 0:
                    f.rotation = Math.PI*0.5;
                    f.position.x = 820;
                    break;
                case 1:
                    f.rotation = Math.PI;
                    f.position.x = 720;
                    f.position.y = 820;
                    break;
                case 2:
                    f.rotation = Math.PI*1.5;
                    f.position.x = -100;
                    f.position.y = 720;
                    break;
                default:
                    f.position.y = -100;
                    break;
            }

            // f.playAnimation({
            //     onCompleteCallback:() => f.playAnimation({repeat:-1, duration:f.totalDuration*1.5}),
            // });
            this.bg_FS_Fires.push(f);
        }

        this.addChild(...this.bg_FS_Fires);
    }

    private atmotl:TimelineMax;
    public playAtmosphere(lvl:number){

        if(!lvl){
            this.atmotl = Helper.timeLineKiller(this.atmotl,new TimelineMax({
                onComplete:() => {
                    this.bg_FS_Fires.forEach(f => f.stop());
                    this.bg_FS_Glow.visible = false;
                }
            }));
            this.atmotl.staggerTo(this.bg_FS_Fires,0.5,{alpha:0},0,0);
            this.atmotl.to(this.bg_FS_Glow,0.5,{alpha:0},0);
            return;
        }

        

        this.atmotl = Helper.timeLineKiller(this.atmotl,new TimelineMax({
            onStart:() =>{
                this.bg_FS_Glow.visible = true;
                if(lvl > 1) this.bg_FS_Fires.forEach( f => {
                    f.visible = true;
                })

            },
            onComplete:() => {
                this.atmoLoopLvl(lvl);
            }
        }));
        if(lvl > 1){
            this.changeBgBlackness(1,0.35);
            this.bg_FS_Fires.forEach(f => f.playAnimation({repeat:-1,duration: f.totalDuration*1.75}))
            this.atmotl.staggerFromTo(this.bg_FS_Fires,1,{alpha:0},{alpha:1},0,0);
        }
        if(!this.bg_FS_Glow.visible)
            this.atmotl.fromTo(this.bg_FS_Glow,1,{alpha:0},{alpha:1},0);
    }

    private atmoLoopLvl(lvl:number = 0){
        this.atmotl = Helper.timeLineKiller(this.atmotl,new TimelineMax({
            onStart:() =>{
                this.bg_FS_Glow.visible = true;
            },
            onComplete:() => {
                if(lvl){
                    this.atmoLoopLvl(lvl);
                }else{
                    this.bg_FS_Glow.visible = false;
                }
            }
        }));
        if(lvl > 1){
            this.atmotl.staggerTo(this.bg_FS_Fires,1.5,{alpha:0.65},0);
            this.atmotl.staggerTo(this.bg_FS_Fires,1.5,{alpha:1},0);
        }
        this.atmotl.to(this.bg_FS_Glow,3,{alpha:0.65});
        this.atmotl.to(this.bg_FS_Glow,3,{alpha:1});
    }

    public playCherring(resized:boolean = false):TimelineMax{
        if(!this.stoppingSpine && !resized) return new TimelineMax();
        this.tl_spine = Helper.timeLineKiller(this.tl_spine,new TimelineMax());
        this.tl_spine.add(this.cheering_spine.play({name: ResizeHelper.IsLandscape() ? "Landcape" : "Portrait", loop:true}));
        this.tl_spine.fromTo(this.cheering_spine,0.9,{alpha:0},{alpha:1},0);
        this.tl_spine.fromTo(this.cheering_spine.position,0.9,{y:460},{y:ResizeHelper.IsLandscape() ? 360 : 400},0);

        this.stoppingSpine = false;
        return this.tl_spine;
    }

    public stopCheering():TimelineMax{
        this.stoppingSpine = true;
        this.tl_spine = Helper.timeLineKiller(this.tl_spine,new TimelineMax());
        this.tl_spine.fromTo(this.cheering_spine,0.5,{alpha:1},{alpha:0},0);
        this.tl_spine.add(() => this.cheering_spine.stop());
        return this.tl_spine;
    }
   
    protected onChangeBackground(data:IGameModeChangedData):boolean {
        if(!super.onChangeBackground(data)) {
            return false;
        }
        return true;
    }

    protected RePaint(posX:number, posY:number, widht:number, height:number):void{
        this._graphOverlay.clear();
        this._graphOverlay.beginFill(0x000000, 1);
        this._graphOverlay.drawRect(posX,posY, widht, height);
        this._graphOverlay.endFill();
    }

    protected RePaint2(posX:number, posY:number, widht:number, height:number):void{
        this._superGraphOverlay.clear();
        this._superGraphOverlay.beginFill(0x000000, 1);
        this._superGraphOverlay.drawRect(posX,posY, widht, height);
        this._superGraphOverlay.endFill();
    }

    protected onResize():void{

        if(!this.stoppingSpine) this.playCherring(true);

        this.cheering_spine.position.y = ResizeHelper.IsLandscape() ? 360 : 400;

        this._reelBackground.scale = SanEngineManager.dim.scale;
        this._reelBackground.position = MathHelper.pointAdd(SanEngineManager.getReelPos(2),new Point(0,SanEngineManager.dim.scale.y * SanSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT));
        
        this.RePaint(-480, -480,2000,2000);
        this.RePaint2(-480, -480,2000,2000);
    }
}